import { useQueries } from '@tanstack/react-query';

import { Grouping } from '../../../telemetry/gen/ai/h2o/cloud/telemetry_read_api/v1/aiunits_pb';
import { Usage } from '../../../telemetry/gen/ai/h2o/cloud/telemetry_read_api/v1/aiunits_pb';
import { useTelemetryService } from '../../../telemetry/hooks';
import { UsageDataParams, UsageDataResult } from '../types';

// All grouping options to prefetch
const ALL_GROUPINGS = [Grouping.WORKSPACE, Grouping.USER, Grouping.SERVICE, Grouping.TAG];

/**
 * Custom hook that prefetches usage data for all grouping options in parallel
 *
 * This hook fetches data for all 4 grouping options (workspace, user, component, tag)
 * simultaneously when the component mounts or when parameters change. The first request
 * (workspace by default) is shown immediately, while the others are cached in the background.
 * When the user changes grouping, the data is served instantly from the cache.
 *
 * @param params Parameters for fetching usage data (dates, granularity, filters)
 * @param currentGroupBy The currently selected grouping option
 * @returns Object containing usage data for current grouping, loading state, error, and refetch function
 */
export function usePrefetchedUsageData(
  params: Omit<UsageDataParams, 'groupBy'>,
  currentGroupBy: Grouping
): UsageDataResult {
  const { startTime, endTime, granularity, selectedServices } = params;
  const TelemetryService = useTelemetryService();

  // Prefetch all grouping options in parallel
  const queries = useQueries({
    queries: ALL_GROUPINGS.map((groupBy) => ({
      queryKey: [
        'usageData',
        {
          startTime: startTime.toISOString(),
          endTime: endTime.toISOString(),
          granularity,
          groupBy,
          servicesKey: selectedServices.length > 0 ? [...selectedServices].sort().join(',') : 'none',
        },
      ],
      queryFn: async (): Promise<Usage[]> => {
        const startTimeISO = startTime.toISOString();
        const endTimeISO = endTime.toISOString();

        // Build filter string from selected services
        let filterString: string | undefined = undefined;
        if (selectedServices.length > 0) {
          filterString = selectedServices.map((svc) => `service = '${svc}'`).join(' OR ');
        }

        const response = await TelemetryService.calculateResourceUsage({
          startTime: startTimeISO,
          endTime: endTimeISO,
          granularity,
          groupBy,
          ...(filterString && { filter: filterString }),
        });

        return response.usage || [];
      },
      // Keep data fresh for 30 minutes
      staleTime: 1000 * 60 * 30,
      // Cache for 30 minutes
      cacheTime: 1000 * 60 * 30,
      // Retry failed requests twice
      retry: 2,
      // Retry delay increases exponentially
      retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
      // Only fetch when component is mounted
      refetchOnWindowFocus: false,
      // Don't refetch on reconnect
      refetchOnReconnect: false,
      // Handle errors
      onError: (error: Error) => {
        console.error('[usePrefetchedUsageData] Error fetching usage data:', error, { groupBy });
      },
    })),
  });

  // Find the query result for the currently selected grouping
  const currentQueryIndex = ALL_GROUPINGS.indexOf(currentGroupBy);
  const currentQuery = queries[currentQueryIndex];

  // Refetch all queries when refetch is called
  const refetchAll = () => {
    queries.forEach((query) => query.refetch());
  };

  return {
    usageData: currentQuery.data,
    isLoading: currentQuery.isLoading,
    error: currentQuery.error || undefined,
    refetch: refetchAll,
  };
}
